// ────────────────────────────────────────────────────────────────────────────
// PROPAGATION D'UN CHANGEMENT DE PRIX CATALOGUE VERS LES DOCUMENTS PROSPACE
//
// Orchestration seulement : ce module résout les accès DB (quels documents,
// quel catalogue, quel taux de commission) puis délègue TOUT le calcul à
// `libs/domain/pro/src/price-propagation.ts`, et écrit le résultat via des RPC
// atomiques (migration 20260801130000).
//
// Appelé depuis les DEUX seuls points par lesquels un prix Pro peut bouger :
//   • SupabaseArticleRepository.update()  (prix de vente, TVA, prix d'achat)
//   • SupabaseProArticlePricingRepository.upsert/upsertMany/delete/deleteMany
//     (paliers Pro — publier, dépublier, remiser)
//
// Les gestes de LOT passent par les variantes `…Many`, qui n'appellent la
// propagation qu'une fois pour toute la sélection.
// ────────────────────────────────────────────────────────────────────────────

import {
  isCommissionRecomputable,
  isDevisRepriceable,
  recomputeCommandeCommission,
  repriceDevisLines,
  PRO_PRICE_EPSILON,
  type CommandeCommissionLine,
  type ProCatalogSnapshot,
} from '@erp/domain/pro';
import { netUnitCostHt } from '@erp/domain/sales';
import type { TypedSupabaseClient } from '../client';

/** Codes des documents réalignés + échecs éventuels, document par document. */
export interface ProPricePropagationResult {
  devisUpdated: string[];
  commandesUpdated: string[];
  failures: Array<{ code: string; message: string }>;
}

/**
 * Champs d'un patch article capables de déplacer un prix ProSpace.
 * Tout le reste (nom, image, stock, catégorie…) ne déclenche aucune propagation.
 */
const PRO_PRICING_FIELDS = [
  'salePriceHt',
  'salePriceTtc',
  'vatRate',
  'purchasePriceHt',
  'supplierDiscount',
] as const;

type ProPricingField = (typeof PRO_PRICING_FIELDS)[number];

export function touchesProPricing(
  patch: Partial<Record<ProPricingField, unknown>>,
): boolean {
  return PRO_PRICING_FIELDS.some((field) => patch[field] !== undefined);
}

/**
 * Motif d'erreur reconnu par les surfaces (erp-web `resolveMutationError`) pour
 * dire « l'article EST enregistré, mais la propagation ProSpace a échoué » —
 * l'écriture de l'article est déjà commitée quand on arrive ici, la faire
 * passer pour un échec complet serait un mensonge.
 */
export const PRO_PRICE_PROPAGATION_ERROR = 'pro_price_propagation_failed';

/** Propage, et transforme un échec en erreur explicitement identifiable. */
export async function runProPricePropagation(
  supabase: TypedSupabaseClient,
  articleIds: readonly string[],
): Promise<void> {
  let outcome: ProPricePropagationResult;
  try {
    outcome = await propagateProPriceChange(supabase, articleIds);
  } catch (error) {
    throw new Error(`${PRO_PRICE_PROPAGATION_ERROR}: ${errorMessage(error)}`);
  }
  if (outcome.failures.length > 0) {
    const detail = outcome.failures.map((f) => `${f.code} (${f.message})`).join(', ');
    throw new Error(`${PRO_PRICE_PROPAGATION_ERROR}: ${detail}`);
  }
}

function emptyResult(): ProPricePropagationResult {
  return { devisUpdated: [], commandesUpdated: [], failures: [] };
}

/** Taille de lot pour les `.in(...)` — au-delà, l'URL PostgREST explose. */
const IN_CHUNK = 300;

function chunk<T>(values: readonly T[], size = IN_CHUNK): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < values.length; i += size) out.push(values.slice(i, i + size));
  return out;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    if (typeof record['message'] === 'string') return record['message'];
  }
  return String(error);
}

/**
 * Réaligne les devis ProSpace et recalcule les commissions des commandes non
 * expédiées qui contiennent l'un des articles dont le prix vient de changer.
 *
 * Ne lève jamais : chaque document est traité indépendamment et les échecs
 * remontent dans `failures`. C'est l'appelant (le repository article) qui
 * décide quoi en faire — l'article, lui, est déjà enregistré.
 */
export async function propagateProPriceChange(
  supabase: TypedSupabaseClient,
  articleIds: readonly string[],
): Promise<ProPricePropagationResult> {
  const result = emptyResult();
  const ids = [...new Set(articleIds.filter(Boolean))];
  if (ids.length === 0) return result;

  const [devisCodes, commandeCodes] = await Promise.all([
    findDocumentCodes(supabase, 'pro_devis_lines', 'devis_code', ids),
    findDocumentCodes(supabase, 'pro_commande_lines', 'commande_code', ids),
  ]);
  if (devisCodes.length === 0 && commandeCodes.length === 0) return result;

  await Promise.all([
    repriceAffectedDevis(supabase, devisCodes, result),
    recomputeAffectedCommandes(supabase, commandeCodes, ids, result),
  ]);

  return result;
}

// ---------------------------------------------------------------- devis ----

interface DevisRow {
  code: string;
  status: string;
  pttc: number;
  typeRemise: string;
  proCustomerId: string;
  lines: Array<{
    articleId: string;
    quantite: number;
    pht: number;
    pttc: number;
    tva: number;
    remise: number;
    montantRemise: number;
  }>;
}

async function repriceAffectedDevis(
  supabase: TypedSupabaseClient,
  codes: string[],
  result: ProPricePropagationResult,
): Promise<void> {
  if (codes.length === 0) return;

  const rows: DevisRow[] = [];
  for (const part of chunk(codes)) {
    const { data, error } = await (supabase as any)
      .from('pro_devis')
      .select('code, status, pttc, type_remise, pro_customer_id, lines:pro_devis_lines(*)')
      .in('code', part);
    if (error) throw error;
    for (const row of (data ?? []) as Array<Record<string, unknown>>) {
      if (!isDevisRepriceable(row['status'] as never)) continue;
      rows.push({
        code: row['code'] as string,
        status: row['status'] as string,
        pttc: Number(row['pttc'] ?? 0),
        typeRemise: (row['type_remise'] as string) ?? 'moy',
        proCustomerId: row['pro_customer_id'] as string,
        lines: ((row['lines'] as Array<Record<string, unknown>> | undefined) ?? [])
          .slice()
          .sort((a, b) => Number(a['line_number'] ?? 0) - Number(b['line_number'] ?? 0))
          .map((line) => ({
            articleId: line['article_id'] as string,
            quantite: Number(line['quantite'] ?? 0),
            pht: Number(line['pht'] ?? 0),
            pttc: Number(line['pttc'] ?? 0),
            tva: Number(line['tva'] ?? 19),
            remise: Number(line['remise'] ?? 0),
            montantRemise: Number(line['montant_remise'] ?? 0),
          })),
      });
    }
  }
  if (rows.length === 0) return;

  // Catalogue de TOUS les articles des devis touchés, pas seulement de ceux
  // qui ont bougé : la commission d'en-tête est un total, la recalculer sans
  // le coût des autres lignes l'effondrerait.
  const [catalog, rates] = await Promise.all([
    fetchCatalog(supabase, rows.flatMap((r) => r.lines.map((l) => l.articleId))),
    fetchCommissionRates(supabase, rows.map((r) => r.proCustomerId)),
  ]);

  for (const devis of rows) {
    if (devis.lines.length === 0) continue;
    try {
      const repriced = repriceDevisLines({
        lines: devis.lines,
        catalog,
        tier: devis.typeRemise as never,
        commissionRatePct: rates.get(devis.proCustomerId) ?? 0,
      });
      if (!repriced.changed) continue;

      const { error } = await (supabase as any).rpc('pro_reprice_devis_atomic', {
        p_code: devis.code,
        p_header: {
          pht: repriced.pht,
          pttc: repriced.pttc,
          tva: repriced.tva,
          total_commission: repriced.totalCommission,
        },
        p_lines: repriced.lines.map((line) => ({
          article_id: line.articleId,
          quantite: line.quantite,
          pht: line.pht,
          pttc: line.pttc,
          tva: line.tva,
          remise: line.remise,
          montant_remise: line.montantRemise,
        })),
      });
      if (error) throw error;
      result.devisUpdated.push(devis.code);

      // Le devis peut avoir été réécrit sans que son TOTAL bouge (deux lignes
      // qui se compensent) : dans ce cas il n'y a rien à annoncer.
      //
      // LIMITE CONNUE : la notification vient APRÈS l'écriture. Si elle échoue,
      // l'échec est signalé, mais un nouvel enregistrement de l'article ne la
      // renverra pas — le devis est désormais aligné, donc plus rien ne bouge.
      // Les prix, eux, sont justes et visibles dans l'app.
      if (Math.abs(repriced.pttc - devis.pttc) > PRO_PRICE_EPSILON) {
        await notifyPriceChange(supabase, 'devis', devis.code, devis.pttc, repriced.pttc);
      }
    } catch (error) {
      result.failures.push({ code: devis.code, message: errorMessage(error) });
    }
  }
}

// ------------------------------------------------------------- commandes ----

interface CommandeRow {
  code: string;
  totalCommission: number;
  proCustomerId: string;
  lines: CommandeCommissionLine[];
}

async function recomputeAffectedCommandes(
  supabase: TypedSupabaseClient,
  codes: string[],
  changedArticleIds: string[],
  result: ProPricePropagationResult,
): Promise<void> {
  if (codes.length === 0) return;

  const rows: CommandeRow[] = [];
  for (const part of chunk(codes)) {
    const { data, error } = await (supabase as any)
      .from('pro_commandes')
      .select('code, status, total_commission, pro_customer_id, lines:pro_commande_lines(*)')
      .in('code', part);
    if (error) throw error;
    for (const row of (data ?? []) as Array<Record<string, unknown>>) {
      // Marchandise partie = vente faite : la commission ne bouge plus.
      if (!isCommissionRecomputable(row['status'] as never)) continue;
      rows.push({
        code: row['code'] as string,
        totalCommission: Number(row['total_commission'] ?? 0),
        proCustomerId: row['pro_customer_id'] as string,
        lines: ((row['lines'] as Array<Record<string, unknown>> | undefined) ?? [])
          .slice()
          .sort((a, b) => Number(a['line_number'] ?? 0) - Number(b['line_number'] ?? 0))
          .map((line) => ({
            id: line['id'] as string,
            articleId: line['article_id'] as string,
            quantite: Number(line['quantite'] ?? 0),
            pht: Number(line['pht'] ?? 0),
            remise: Number(line['remise'] ?? 0),
            unitCostHt: line['unit_cost_ht'] != null ? Number(line['unit_cost_ht']) : null,
          })),
      });
    }
  }
  if (rows.length === 0) return;

  // Ici, seuls les articles MODIFIÉS comptent : les autres lignes gardent le
  // coût figé qu'elles portent déjà.
  const [catalog, rates] = await Promise.all([
    fetchCatalog(supabase, changedArticleIds),
    fetchCommissionRates(supabase, rows.map((r) => r.proCustomerId)),
  ]);
  const newCostByArticle = new Map<string, number | null>();
  for (const [articleId, snapshot] of catalog) {
    newCostByArticle.set(articleId, snapshot.netCostHt);
  }

  for (const commande of rows) {
    if (commande.lines.length === 0) continue;
    try {
      const recomputed = recomputeCommandeCommission({
        lines: commande.lines,
        newNetCostByArticle: newCostByArticle,
        commissionRatePct: rates.get(commande.proCustomerId) ?? 0,
      });
      if (!recomputed.changed) continue;

      const moved = new Set(recomputed.moves.map((m) => m.articleId));
      const costs = recomputed.costs
        .filter((cost) => moved.has(cost.articleId) && cost.unitCostHt != null)
        .map((cost) => ({
          id: cost.id,
          article_id: cost.articleId,
          unit_cost_ht: cost.unitCostHt,
        }));
      if (costs.length === 0) continue;

      const { error } = await (supabase as any).rpc('pro_apply_commande_costs_atomic', {
        p_code: commande.code,
        p_costs: costs,
        p_total_commission: recomputed.totalCommission,
      });
      if (error) throw error;
      result.commandesUpdated.push(commande.code);

      // Un coût peut bouger sans changer la commission (marge déjà nulle des
      // deux côtés) — inutile d'alerter l'électricien pour rien.
      if (Math.abs(recomputed.totalCommission - commande.totalCommission) > PRO_PRICE_EPSILON) {
        await notifyPriceChange(
          supabase,
          'commande',
          commande.code,
          commande.totalCommission,
          recomputed.totalCommission,
        );
      }
    } catch (error) {
      result.failures.push({ code: commande.code, message: errorMessage(error) });
    }
  }
}

// ----------------------------------------------------------------- utils ----

async function findDocumentCodes(
  supabase: TypedSupabaseClient,
  table: 'pro_devis_lines' | 'pro_commande_lines',
  column: 'devis_code' | 'commande_code',
  articleIds: string[],
): Promise<string[]> {
  const codes = new Set<string>();
  for (const part of chunk(articleIds)) {
    const { data, error } = await (supabase as any)
      .from(table)
      .select(column)
      .in('article_id', part);
    if (error) throw error;
    for (const row of (data ?? []) as Array<Record<string, unknown>>) {
      const code = row[column];
      if (typeof code === 'string') codes.add(code);
    }
  }
  return [...codes];
}

async function fetchCatalog(
  supabase: TypedSupabaseClient,
  articleIds: string[],
): Promise<Map<string, ProCatalogSnapshot>> {
  const ids = [...new Set(articleIds.filter(Boolean))];
  const catalog = new Map<string, ProCatalogSnapshot>();
  if (ids.length === 0) return catalog;

  for (const part of chunk(ids)) {
    const [articles, pricings] = await Promise.all([
      (supabase as any)
        .from('authorized_articles')
        .select('id, sale_price_ht, vat_rate, purchase_price_ht, supplier_discount')
        .in('id', part),
      (supabase as any)
        .from('pro_article_pricing')
        .select('article_id, prix_pro_min, prix_pro_moy, prix_pro_max, remise_min, remise_moy, remise_max, updated_at')
        .in('article_id', part),
    ]);
    if (articles.error) throw articles.error;
    if (pricings.error) throw pricings.error;

    const pricingByArticle = new Map<string, ProCatalogSnapshot['pricing']>();
    for (const row of (pricings.data ?? []) as Array<Record<string, unknown>>) {
      pricingByArticle.set(row['article_id'] as string, {
        articleId: row['article_id'] as string,
        prixProMin: row['prix_pro_min'] != null ? Number(row['prix_pro_min']) : null,
        prixProMoy: row['prix_pro_moy'] != null ? Number(row['prix_pro_moy']) : null,
        prixProMax: row['prix_pro_max'] != null ? Number(row['prix_pro_max']) : null,
        remiseMin: Number(row['remise_min'] ?? 0),
        remiseMoy: Number(row['remise_moy'] ?? 0),
        remiseMax: Number(row['remise_max'] ?? 0),
        updatedAt: (row['updated_at'] as string) ?? '',
      });
    }

    for (const row of (articles.data ?? []) as Array<Record<string, unknown>>) {
      const articleId = row['id'] as string;
      catalog.set(articleId, {
        articleId,
        salePriceHt: Number(row['sale_price_ht'] ?? 0),
        // Même repli que le catalogue Pro et que pro_line_pricing_guard : une
        // TVA nulle en base vaut 19 %, sinon les prix ne concordent pas.
        vatRate: row['vat_rate'] != null ? Number(row['vat_rate']) : 19,
        netCostHt: netUnitCostHt({
          purchasePriceHt: Number(row['purchase_price_ht'] ?? 0),
          supplierDiscount: Number(row['supplier_discount'] ?? 0),
        }),
        pricing: pricingByArticle.get(articleId) ?? null,
      });
    }
  }
  return catalog;
}

/**
 * Taux effectif par électricien, lu PAR ID.
 *
 * Jamais via `proCustomers.listForTenant()`, qui filtre `is_preview = false` :
 * une commande du compte Aperçu retomberait à un taux 0 et sa commission serait
 * écrasée — c'est exactement le bug de commission à zéro de 20260713100000.
 */
async function fetchCommissionRates(
  supabase: TypedSupabaseClient,
  proCustomerIds: string[],
): Promise<Map<string, number>> {
  const ids = [...new Set(proCustomerIds.filter(Boolean))];
  const rates = new Map<string, number>();
  if (ids.length === 0) return rates;

  for (const part of chunk(ids)) {
    const { data, error } = await (supabase as any)
      .from('pro_customers')
      .select('id, commission_rate')
      .in('id', part);
    if (error) throw error;
    for (const row of (data ?? []) as Array<Record<string, unknown>>) {
      rates.set(row['id'] as string, Number(row['commission_rate'] ?? 0));
    }
  }
  return rates;
}

async function notifyPriceChange(
  supabase: TypedSupabaseClient,
  kind: 'devis' | 'commande',
  code: string,
  oldAmount: number,
  newAmount: number,
): Promise<void> {
  const { error } = await (supabase as any).rpc('pro_notify_price_change', {
    p_kind: kind,
    p_code: code,
    p_metadata: { code, oldAmount, newAmount },
  });
  if (error) throw error;
}
